'use client';
import { createContext, useContext, useState, useCallback } from 'react';
import { ChatHistory } from '@/types/types';

interface ChatContextType {
  chats: ChatHistory[];
  refreshChats: () => Promise<void>;
  currentChat?: ChatHistory;
  setCurrentChat: (chat: ChatHistory) => void;
  addChat: (chat: ChatHistory) => void;
  updateChat: (updatedChat: ChatHistory) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [chats, setChats] = useState<ChatHistory[]>([]);
  const [currentChat, setCurrentChat] = useState<ChatHistory | undefined>();

  const refreshChats = useCallback(async () => {
    const resp = await fetch('/api/auth/getsigninuser');
    const userData = await resp.json();

    if (!userData) {
      throw new Error('Unauthorized');
    }
    const response = await fetch('/api/chat/get-chats', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: userData.signedInUserId,
      }),
    });
    const data = await response.json();

    if (data.chats) {
      setChats(data.chats);
      if (currentChat) {
        const updatedCurrentChat = data.chats.find((chat: ChatHistory) => chat.id === currentChat.id);
        if (updatedCurrentChat) {
          setCurrentChat(updatedCurrentChat);
        }
      }
    }
  }, [currentChat]);

  const addChat = useCallback((newChat: ChatHistory) => {
    setChats(prev => {
      // Check if chat already exists
      const exists = prev.some(chat => chat.id === newChat.id);
      if (exists) {
        return prev; // Don't add duplicate
      }
      return [newChat, ...prev];
    });
  }, []);

  const updateChat = useCallback(
    (updatedChat: ChatHistory) => {
      setChats(prev => prev.map(chat => (chat.id === updatedChat.id ? updatedChat : chat)));
      if (currentChat?.id === updatedChat.id) {
        setCurrentChat(updatedChat);
      }
    },
    [currentChat]
  );

  return (
    <ChatContext.Provider
      value={{
        chats,
        refreshChats,
        currentChat,
        setCurrentChat,
        addChat,
        updateChat,
      }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
